import React from "react";
import { useTranslation } from "react-i18next";
import type { QuickLoginController } from "../hooks/useQuickLogin";
import type {
  QuickLoginFlow,
  QuickLoginStep,
} from "../hooks/useQuickLoginFlow";
import { ChoosePin } from "./ChoosePin";
import { LinkButton } from "./LinkButton";
import { PinLogin } from "./PinLogin";
import { QuickLoginPrompt } from "./QuickLoginPrompt";
import { SavedAccountPassword } from "./SavedAccountPassword";
import { SavedAccounts } from "./SavedAccounts";

export interface QuickLoginStepsProps {
  step: string;
  quickLogin: QuickLoginController | null;
  flow: QuickLoginFlow;
  goTo(step: QuickLoginStep | "forgot-request"): void;
  onLoginForm(): void;
  onPasswordSubmit(email: string, password: string): void;
}

export const QuickLoginSteps: React.FC<QuickLoginStepsProps> = ({
  step,
  quickLogin,
  flow,
  goTo,
  onLoginForm,
  onPasswordSubmit,
}) => {
  const { t } = useTranslation();
  const { pin } = flow;
  const account = pin.account;
  const hasAccounts = !!quickLogin?.rows.length;

  return (
    <>
      {(step === "login" || step === "pin" || step === "account-password") &&
        hasAccounts && (
          <div className="w-[300px]">
            <LinkButton onClick={() => goTo("accounts")} disabled={pin.pending}>
              {t("Back to saved accounts")}
            </LinkButton>
          </div>
        )}
      {step === "accounts" && quickLogin && (
        <SavedAccounts
          rows={quickLogin.rows}
          online={quickLogin.online}
          onSelect={flow.selectAccount}
          onForget={(row) => flow.forgetAccount(row, false)}
          onDifferentEmail={onLoginForm}
        />
      )}
      {step === "pin" && quickLogin && account && (
        <PinLogin
          email={account.email}
          value={pin.value}
          onChange={pin.change}
          error={pin.error}
          disabled={pin.disabled}
          online={quickLogin.online}
          onPassword={() => goTo("account-password")}
          onForget={() => flow.forgetAccount(account, true)}
        />
      )}
      {step === "account-password" && quickLogin && account && (
        <SavedAccountPassword
          email={account.email}
          online={quickLogin.online}
          onSubmit={(password) => onPasswordSubmit(account.email, password)}
          onForgotPassword={() => goTo("forgot-request")}
          onDifferentEmail={onLoginForm}
        />
      )}
      {step === "enrol-prompt" && (
        <QuickLoginPrompt
          onSetUp={() => goTo("pin-choose")}
          onNotNow={flow.skip}
          disabled={flow.enrolmentPending}
        />
      )}
      {step === "pin-choose" && (
        <ChoosePin
          onComplete={flow.enrol}
          onSkip={flow.skip}
          disabled={flow.enrolmentPending}
        />
      )}
    </>
  );
};
